import type { HireLocationDetail } from "~/lib/marketplace/hire-recommendations-pricing";

/** Default business hours prefilled when adding a hire-flow location. */
export const DEFAULT_HIRE_LOCATION_BUSINESS_HOURS = "Mon-Fri 8 AM - 5 PM";
import type { DetectedWebsiteLocation } from "~/lib/marketplace/website-location-utils";
import type { HireRecommendationsSelection } from "~/lib/marketplace/hire-recommendations-pricing";

function extractPostalCode(loc: DetectedWebsiteLocation): string | undefined {
  const caPostal = loc.displayLabel.match(/\b([A-Z]\d[A-Z]\s?\d[A-Z]\d)\b/i)?.[1];
  if (caPostal) return caPostal.replace(/\s/g, " ").trim().toUpperCase();

  const usZip =
    loc.displayLabel.match(/\b(\d{5}(?:-\d{4})?)\b/)?.[1] ??
    loc.address.match(/\b(\d{5}(?:-\d{4})?)\b/)?.[1];
  return usZip;
}

export function detectedLocationsToHireDetails(
  locations: DetectedWebsiteLocation[],
  defaultLeadEmail?: string,
): HireLocationDetail[] {
  return locations.map((loc) => ({
    name:
      loc.name?.trim() ||
      loc.city?.trim() ||
      loc.displayLabel.split("—")[0]?.trim() ||
      "Main Office",
    address: loc.address?.trim() || undefined,
    city: loc.city?.trim() || undefined,
    state: loc.state?.trim() || undefined,
    postalCode: extractPostalCode(loc),
    businessHours: DEFAULT_HIRE_LOCATION_BUSINESS_HOURS,
    leadTeamMemberEmail: defaultLeadEmail,
    otherTeamMemberEmails: [],
  }));
}

export function formatHireLocationLabel(detail: HireLocationDetail): string {
  const cityState = [detail.city, detail.state].filter(Boolean).join(", ");
  if (detail.name && cityState) return `${detail.name} — ${cityState}`;
  if (cityState) return cityState;
  return detail.name;
}

export function buildDefaultHireLocationDetail(args: {
  companyName: string;
  leadTeamMemberEmail?: string;
  phone?: string;
  businessHours?: string;
}): HireLocationDetail {
  const name = args.companyName.trim() || "Main Office";
  return {
    name,
    phone: args.phone?.trim() || undefined,
    businessHours: args.businessHours?.trim() || DEFAULT_HIRE_LOCATION_BUSINESS_HOURS,
    leadTeamMemberEmail: args.leadTeamMemberEmail?.trim() || undefined,
    otherTeamMemberEmails: [],
  };
}

export function padHireLocationDetails(
  details: HireLocationDetail[],
  locationCount: number,
  fallbackCompanyName = "Main Office",
): HireLocationDetail[] {
  const padded = [...details];
  while (padded.length < locationCount) {
    padded.push(
      buildDefaultHireLocationDetail({
        companyName:
          padded.length === 0
            ? fallbackCompanyName
            : `${fallbackCompanyName} — Location ${padded.length + 1}`,
      }),
    );
  }
  return padded.slice(0, locationCount);
}

/** Ensure locationCount always has displayable rows with lead assignment. */
export function ensureHireLocationDetailsInSelection(
  selection: HireRecommendationsSelection,
  args: {
    companyName: string;
    leadTeamMemberEmail?: string;
    phone?: string;
  },
): HireRecommendationsSelection {
  const leadEmail = args.leadTeamMemberEmail?.trim();
  const count = Math.max(1, selection.locationCount);
  const existing = selection.locationDetails.slice(0, count);

  const withLead = existing.map((loc) => ({
    ...loc,
    leadTeamMemberEmail: loc.leadTeamMemberEmail?.trim() || leadEmail || undefined,
    businessHours: loc.businessHours?.trim() || DEFAULT_HIRE_LOCATION_BUSINESS_HOURS,
  }));

  const details =
    withLead.length >= count && withLead.some((loc) => loc.name?.trim())
      ? withLead
      : Array.from({ length: count }, (_, index) => {
          const prior = withLead[index];
          if (prior?.name?.trim()) {
            return {
              ...prior,
              leadTeamMemberEmail: prior.leadTeamMemberEmail || leadEmail || undefined,
              businessHours: prior.businessHours?.trim() || DEFAULT_HIRE_LOCATION_BUSINESS_HOURS,
            };
          }
          return buildDefaultHireLocationDetail({
            companyName: index === 0 ? args.companyName : `${args.companyName} — Location ${index + 1}`,
            leadTeamMemberEmail: leadEmail,
            phone: args.phone,
          });
        });

  return {
    ...selection,
    locationCount: count,
    locationDetails: details,
    locationList: details.map((detail) => formatHireLocationLabel(detail)),
  };
}
